import { useLazyQuery, useMutation } from '@apollo/client';
import { useTranslations } from 'next-intl';
import { startTransition, useEffect, useRef, useState } from 'react';
import { destroyAuthConditionCookies, persistAuthConditionCookies } from './auth-cookies';
import { consumeIdpCallbackUrl, isPendingDirectIdpResolve } from './idp-resolve-storage';
import type { IdpResolveOrigin, MigrationModalType, MigrationSubmitValues } from './types';
import { GetMeIsEmailVerifiedDocument } from '@/__generated__/graphql';
import {
  type AccountMigrationRequiredField,
  AccountMigrationRequirementsDocument,
  CreateAccountFromIdpDocument,
  type IdpSignInProfilePreview,
  LinkAccountWithIdpDocument,
  MigrateCurrentAccountToIdpDocument,
  MigrateCurrentAccountToIdpStatus,
  ResolveIdpSignInDocument,
  ResolveIdpSignInStatus,
} from '@/__generated__/idp/graphql';
import { PostSignInAPI } from '@/apis';
import { PATHS, QUERIES } from '@/constants/path';
import { useRouter } from '@/i18n/routing';
import { getIdpApolloClient } from '@/lib/apollo/idp-client';
import useDialog from '@/providers/dialog-provider';
import { localStorage } from '@/utils/local-storage';

type UseIdpMigrationParams = {
  isRegistered: boolean;
  isIdpAuthenticated: boolean;
  idpSessionStatus: 'authenticated' | 'loading' | 'unauthenticated';
  isInitializationComplete: boolean;
  magicAuthReady: boolean;
  magicAuthenticated: boolean;
  token?: string;
  magicLogout: () => Promise<void>;
  setIsRegistered: (value: boolean) => void;
  setIsEmailVerified: (value: boolean) => void;
  setIsLoginLoading: (value: boolean) => void;
  getMeIsEmailVerified: () => Promise<unknown>;
};

export function useIdpMigration({
  isRegistered,
  isIdpAuthenticated,
  idpSessionStatus,
  isInitializationComplete,
  magicAuthReady,
  magicAuthenticated,
  token,
  magicLogout,
  setIsRegistered,
  setIsEmailVerified,
  setIsLoginLoading,
  getMeIsEmailVerified,
}: UseIdpMigrationParams) {
  const t = useTranslations();
  const { push } = useRouter();
  const { showDialog } = useDialog();

  const [isMigrationNotFoundModalOpen, setIsMigrationNotFoundModalOpen] = useState(false);
  const [migrationAuthStatus, setMigrationAuthStatus] = useState<ResolveIdpSignInStatus | null>(
    null,
  );

  const [pendingMigrationResolve, setPendingMigrationResolve] = useState(false);
  const [idpResolveOrigin, setIdpResolveOrigin] = useState<IdpResolveOrigin | null>(null);
  const [migrationResolveHandled, setMigrationResolveHandled] = useState(false);

  const startIdpResolve = (origin: IdpResolveOrigin) => {
    setPendingMigrationResolve(true);
    setIdpResolveOrigin(origin);
  };
  const [migrationModal, setMigrationModal] = useState<MigrationModalType>(null);
  const [migrationProfilePreview, setMigrationProfilePreview] = useState<IdpSignInProfilePreview[]>(
    [],
  );
  const [migrationRequiredFields, setMigrationRequiredFields] = useState<
    AccountMigrationRequiredField[]
  >([]);
  const [migrationPasswordError, setMigrationPasswordError] = useState<string | null>(null);
  const [fetchResolveIdpSignIn] = useLazyQuery(ResolveIdpSignInDocument, {
    client: getIdpApolloClient(),
    fetchPolicy: 'network-only',
  });
  const [fetchAccountMigrationRequirements] = useLazyQuery(AccountMigrationRequirementsDocument, {
    client: getIdpApolloClient(),
    fetchPolicy: 'network-only',
  });
  const [getMeIsEmailVerifiedIdp] = useLazyQuery(GetMeIsEmailVerifiedDocument, {
    client: getIdpApolloClient(),
    fetchPolicy: 'network-only',
  });

  const [createAccountFromIdp] = useMutation(CreateAccountFromIdpDocument, {
    client: getIdpApolloClient(),
  });
  const [linkAccountWithIdp] = useMutation(LinkAccountWithIdpDocument, {
    client: getIdpApolloClient(),
  });
  const [migrateCurrentAccountToIdp] = useMutation(MigrateCurrentAccountToIdpDocument, {
    client: getIdpApolloClient(),
  });

  const [pendingDirectIdpResolve, setPendingDirectIdpResolve] =
    useState<boolean>(isPendingDirectIdpResolve);

  // 直接パス(IdP サインイン後の戻り)の resolve を処理中/処理済みかを保持する。
  // pendingDirectIdpResolve は resolve 開始直後に false へ倒すため、それだけでは
  // 下の me フォールバック useEffect のガードが外れて getMe → setIsRegistered(true) が
  // 走り、window.location.assign によるリロード前にクライアント状態だけログイン済みへ
  // 変わってしまう（未ログイン↔ログイン済みのチラつきの一因）。ref でその競合を防ぐ。
  const isHandlingDirectResolveRef = useRef(isPendingDirectIdpResolve());

  const routeIdpStatusToModal = async (
    status: ResolveIdpSignInStatus,
    handlers: {
      onAlreadyLinked: () => void | Promise<void>;
      onUnhandled?: () => void;
    },
  ) => {
    switch (status) {
      case ResolveIdpSignInStatus.AlreadyLinked:
        await handlers.onAlreadyLinked();
        break;
      case ResolveIdpSignInStatus.AccountFoundUnlinked:
        setMigrationModal('terms');
        break;
      case ResolveIdpSignInStatus.AccountNotFound:
        setMigrationModal('migration');
        break;
      case ResolveIdpSignInStatus.InvalidToken:
        break;
      default:
        handlers.onUnhandled?.();
        break;
    }
  };

  useEffect(() => {
    (async () => {
      const interrupt =
        !isInitializationComplete ||
        isRegistered ||
        !magicAuthReady ||
        !magicAuthenticated ||
        !token ||
        migrationResolveHandled;

      if (interrupt) return;
      try {
        // hook 引数の isRegistered と混同しないよう、API レスポンスは api* 接頭辞で受ける。
        const {
          ok,
          isEmailVerified: apiIsEmailVerified,
          isRegistered: apiIsRegistered,
        } = await PostSignInAPI(token);
        if (ok) {
          const isMigrationResolve = apiIsRegistered && pendingMigrationResolve;
          if (!isMigrationResolve) {
            startTransition(() => {
              setIsRegistered(apiIsRegistered);
              setIsEmailVerified(apiIsEmailVerified);
            });
          }
          if (!apiIsRegistered) {
            setIsMigrationNotFoundModalOpen(true);
          } else if (pendingMigrationResolve) {
            setPendingMigrationResolve(false);
            setMigrationResolveHandled(true);
            try {
              const { data } = await fetchAccountMigrationRequirements();
              const requirements = data?.accountMigrationRequirements;
              if (requirements) {
                setMigrationRequiredFields(requirements.requiredFields);
                setMigrationModal('terms');
              } else {
                showDialog({
                  title: t('common-error-title'),
                  body: t('login-error-description'),
                });
              }
            } catch (error) {
              console.error('accountMigrationRequirements failed', error);
              showDialog({
                title: t('common-error-title'),
                body: t('login-error-description'),
              });
            } finally {
              destroyAuthConditionCookies();
            }
          }
        }
      } catch (error) {
        console.error(error);
        showDialog({
          title: t('common-error-title'),
          body: t('login-error-description'),
        });
      } finally {
        setIsLoginLoading(false);
      }
    })();
    // 認証状態の変化を起点に一度だけ実行させたいフロー。setter / showDialog / t /
    // fetchAccountMigrationRequirements は安定参照 or 実行時最新で足りるため依存に含めない。
  }, [
    magicAuthenticated,
    magicAuthReady,
    isRegistered,
    token,
    isInitializationComplete,
    pendingMigrationResolve,
    idpResolveOrigin,
    migrationResolveHandled,
  ]);

  useEffect(() => {
    if (!pendingDirectIdpResolve) return;

    // NextAuth セッションの確定を待つ。確定前に resolve すると未認証と誤判定してしまう。
    if (idpSessionStatus === 'loading') return;

    if (!isIdpAuthenticated) {
      setPendingDirectIdpResolve(false);
      isHandlingDirectResolveRef.current = false;
      try {
        sessionStorage.removeItem('pending_idp_resolve');
      } catch {
        // sessionStorage が使えない環境では無視
      }
      return;
    }

    (async () => {
      isHandlingDirectResolveRef.current = true;
      setPendingDirectIdpResolve(false);
      sessionStorage.removeItem('pending_idp_resolve');

      setIdpResolveOrigin('idp');

      // ハードリダイレクト(window.location.assign)を行う場合はページ遷移が
      // 完了するまでローディングを維持し、未ログインUIのチラつきを防ぐ。
      let willRedirect = false;
      try {
        setIsLoginLoading(true);
        const { data } = await fetchResolveIdpSignIn();
        const status = data?.resolveIdpSignIn?.status;
        if (status) {
          setMigrationAuthStatus(status);
          setMigrationProfilePreview(data?.resolveIdpSignIn?.profilePreview ?? []);
          await routeIdpStatusToModal(status, {
            onAlreadyLinked: async () => {
              const { data: meData } = await getMeIsEmailVerifiedIdp();
              if (meData?.me?.isEmailVerified) {
                persistAuthConditionCookies();

                willRedirect = true;
                window.location.assign(consumeIdpCallbackUrl());
              } else {
                showDialog({
                  title: t('common-error-title'),
                  body: t('login-error-description'),
                });
              }
            },
            onUnhandled: () =>
              showDialog({
                title: t('common-error-title'),
                body: t('login-error-description'),
              }),
          });
        }
      } catch (error) {
        console.error('resolveIdpSignIn failed (direct path)', error);
        showDialog({
          title: t('common-error-title'),
          body: t('login-error-description'),
        });
      } finally {
        if (!willRedirect) setIsLoginLoading(false);
      }
    })();
    // pendingDirectIdpResolve の立ち上がり(direct パス開始)だけを起点にする。
    // 参照している fetcher / setter / showDialog / t は依存に含めず、多重実行を避ける。
  }, [pendingDirectIdpResolve, isIdpAuthenticated, idpSessionStatus]);

  // IdP(NextAuth/OIDC)セッションはあるが KLEW 側の登録状態が未確定の場合
  //（既ログイン IdP ユーザーのリロード/再訪、認証条件 Cookie 欠落時など）に、
  // me を取得してログイン状態（isRegistered/isEmailVerified）と永続 Cookie を復元する。
  useEffect(() => {
    // 直接パスの resolve を処理中/処理済みの間は、そちらのフロー
    //（リダイレクト or 移行モーダル）に任せて me フォールバックは走らせない。
    if (
      !isIdpAuthenticated ||
      isRegistered ||
      pendingDirectIdpResolve ||
      isHandlingDirectResolveRef.current
    )
      return;
    (async () => {
      try {
        const { data } = await getMeIsEmailVerifiedIdp();
        if (data?.me) {
          startTransition(() => {
            setIsRegistered(true);
            setIsEmailVerified(!!data.me?.isEmailVerified);
          });
          persistAuthConditionCookies();
        }
      } catch (error) {
        console.error('IdP me フォールバック取得に失敗しました', error);
      }
    })();
    // IdP 認証状態 / 登録状態 / direct パス処理中フラグの変化のみを起点にする。
    // getMeIsEmailVerifiedIdp / setter は依存に含めず、フォールバックの多重実行を避ける。
  }, [isIdpAuthenticated, isRegistered, pendingDirectIdpResolve]);

  const handleMigrationSubmit = async (values?: MigrationSubmitValues) => {
    try {
      if (idpResolveOrigin === 'migration') {
        if (!values) return;
        setMigrationPasswordError(null);
        const { data } = await migrateCurrentAccountToIdp({
          variables: {
            input: {
              password: values.password,
              termsAccepted: values.termsAccepted,
              marketingOptIn: values.marketingOptIn,
            },
          },
        });
        const result = data?.migrateCurrentAccountToIdp;
        if (result?.__typename === 'MigrateCurrentAccountToIdpPayloadSuccess') {
          if (result.status === MigrateCurrentAccountToIdpStatus.Created) {
            persistAuthConditionCookies();
            setMigrationModal('thankYou');
          } else if (result.status === MigrateCurrentAccountToIdpStatus.IdpAccountAlreadyExists) {
            setMigrationModal('emailAlreadyRegistered');
          } else {
            // 想定外のステータス（BE 仕様変更等）でも無反応で詰まないよう汎用エラーを表示する
            showDialog({
              title: t('common-error-title'),
              body: t('login-error-description'),
            });
          }
        } else if (result?.__typename === 'ErrorValidationFailed') {
          setMigrationPasswordError(result.message || t('login-error-description'));
        } else {
          showDialog({
            title: t('common-error-title'),
            body: result?.message ?? t('login-error-description'),
          });
        }
      } else {
        const { data } = await linkAccountWithIdp();
        const result = data?.linkAccountWithIdp;
        if (result?.__typename === 'LinkAccountWithIdpPayloadSuccess' && result.ok) {
          persistAuthConditionCookies();
          await getMeIsEmailVerified();
          setMigrationModal('thankYou');
        } else {
          showDialog({
            title: t('common-error-title'),
            body:
              result && result.__typename !== 'LinkAccountWithIdpPayloadSuccess'
                ? result.message
                : t('login-error-description'),
          });
        }
      }
    } catch (error) {
      console.error('移行処理に失敗しました', error);
      showDialog({
        title: t('common-error-title'),
        body: t('login-error-description'),
      });
    }
  };

  const closeMigrationModal = () => {
    const shouldDiscardMigrationSession =
      idpResolveOrigin === 'migration' && migrationModal !== 'thankYou';
    setMigrationModal(null);
    setIdpResolveOrigin(null);
    setMigrationRequiredFields([]);
    setMigrationPasswordError(null);
    if (shouldDiscardMigrationSession) {
      magicLogout();
      destroyAuthConditionCookies();
    }
  };

  /**
   * 移行パス(B) CREATED 完了後の「トップページに戻る」ハンドラ。
   * 移行で発行済みのログイン状態 Cookie を維持したままトップへ戻る。
   * （LIVENODE への signIn リダイレクトは行わない）
   */
  const handleMigrationThankYouComplete = () => {
    closeMigrationModal();
    startTransition(() => {
      setIsRegistered(true);
      setIsEmailVerified(true);
    });
    push(PATHS.ROOT);
  };

  /**
   * IdP 直接パス（AccountNotFound）: MigrationProfileModal（「もう少しです」）の
   * 「KLEWを始める」送信ハンドラ。
   * createAccountFromIdp を実行し、成功したら元ページへ戻して welcome モーダルを表示する。
   * BE は access_token Cookie 経由で /userinfo から sub + プロフィールを補完して新規作成する。
   */
  const handleCreateAccountFromIdp = async (data: {
    realFamilyName: string;
    realFirstName: string;
    realFamilyNameKana: string;
    realFirstNameKana: string;
    receiveNewsletter: boolean;
  }) => {
    try {
      const res = await createAccountFromIdp({
        variables: {
          input: {
            realFamilyName: data.realFamilyName,
            realFirstName: data.realFirstName,
            realFamilyNameKana: data.realFamilyNameKana || undefined,
            realFirstNameKana: data.realFirstNameKana || undefined,
            enableRecommendationNotification: data.receiveNewsletter,
          },
        },
      });

      const result = res.data?.createAccountFromIdp;
      if (result?.__typename !== 'CreateAccountFromIdpPayloadSuccess') {
        showDialog({
          title: t('common-error-title'),
          body: result ? result.message : t('login-error-description'),
        });
        return;
      }

      persistAuthConditionCookies();

      const isIdpOrigin = idpResolveOrigin === 'idp';
      closeMigrationModal();
      const welcomeQuery = `${QUERIES.WELCOME_MODAL.key}=${QUERIES.WELCOME_MODAL.value}`;
      if (isIdpOrigin) {
        const callbackUrl = consumeIdpCallbackUrl();
        const separator = callbackUrl.includes('?') ? '&' : '?';
        window.location.assign(`${callbackUrl}${separator}${welcomeQuery}`);
      } else {
        startTransition(() => {
          setIsRegistered(true);
          setIsEmailVerified(true);
        });
        const callbackUrl = localStorage('callbackUrl').getValue();
        push(`${callbackUrl || PATHS.ROOT}?${welcomeQuery}`);
      }
    } catch (error) {
      console.error('createAccountFromIdp に失敗しました', error);
      showDialog({
        title: t('common-error-title'),
        body: t('login-error-description'),
      });
    }
  };

  const handleThankYouClose = () => {
    const shouldRedirect = idpResolveOrigin === 'idp';
    closeMigrationModal();
    if (shouldRedirect) {
      window.location.assign(consumeIdpCallbackUrl());
    }
  };

  return {
    startIdpResolve,

    resetMigrationResolveHandled: () => setMigrationResolveHandled(false),
    isMigrationNotFoundModalOpen,
    setIsMigrationNotFoundModalOpen,
    migrationModal,
    setMigrationModal,
    migrationAuthStatus,
    migrationProfilePreview,
    migrationRequiredFields,
    migrationPasswordError,
    idpResolveOrigin,
    handleMigrationSubmit,
    closeMigrationModal,
    handleMigrationThankYouComplete,
    handleCreateAccountFromIdp,
    handleThankYouClose,
  };
}
